<template>
  <div class="page-card product-flow-page">
    <div class="page-head">
      <div>
        <h2 class="page-title">商品流水</h2>
        <div class="page-subtitle">查看商品销售排行、收款与退款统计，并下钻单品订单流水</div>
      </div>
      <el-button type="primary" icon="el-icon-refresh" @click="fetchAll">刷新</el-button>
    </div>

    <div class="summary-grid">
      <div class="summary-item">
        <div class="summary-label">销量合计</div>
        <div class="summary-value">{{ summary.salesQuantity || 0 }}</div>
      </div>
      <div class="summary-item income">
        <div class="summary-label">收款合计</div>
        <div class="summary-value">¥{{ formatMoney(summary.paidAmount) }}</div>
      </div>
      <div class="summary-item expense">
        <div class="summary-label">退款合计</div>
        <div class="summary-value">¥{{ formatMoney(summary.refundAmount) }}</div>
      </div>
      <div class="summary-item count">
        <div class="summary-label">支付订单数</div>
        <div class="summary-value">{{ summary.orderCount || 0 }}</div>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 260px"
        />
        <el-cascader
          v-model="query.categoryId"
          :options="categoryOptions"
          :props="categoryCascaderProps"
          placeholder="商品分类"
          style="width: 220px"
          clearable
          filterable
        />
        <el-input
          v-model="query.keyword"
          clearable
          placeholder="商品标题/订单号"
          style="width: 220px"
          @keyup.enter.native="handleSearch"
        />
        <el-select v-model="query.sortField" style="width: 140px">
          <el-option label="按销量排序" value="salesQuantity" />
          <el-option label="按收款排序" value="paidAmount" />
          <el-option label="按退款排序" value="refundAmount" />
        </el-select>
        <el-button type="primary" @click="handleSearch">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
        <el-button size="small" :loading="exporting" @click="exportFlowOrders">导出对账</el-button>
      </div>
    </div>

    <el-table v-loading="rankingLoading" :data="rankingList" border>
      <el-table-column prop="rankNo" label="排名" width="70" align="center" />
      <el-table-column label="商品" min-width="260">
        <template slot-scope="{ row }">
          <div class="product-cell">
            <img v-if="row.coverUrl" class="product-cover" :src="fullUrl(row.coverUrl)" alt="">
            <div v-else class="product-cover product-cover-empty">封面</div>
            <div class="product-info">
              <div class="product-title">{{ row.title || '-' }}</div>
              <div class="product-subtitle">{{ row.categoryName || '-' }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="salesQuantity" label="销量" width="100" align="center" />
      <el-table-column label="收款金额" width="130" align="right">
        <template slot-scope="{ row }">
          <span class="amount-income">¥{{ formatMoney(row.paidAmount) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="退款金额" width="130" align="right">
        <template slot-scope="{ row }">
          <span class="amount-expense">¥{{ formatMoney(row.refundAmount) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="120" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openProductFlow(row)">查看流水</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager-wrap">
      <el-pagination
        background
        layout="total, prev, pager, next, sizes"
        :current-page="query.page"
        :page-size="query.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="rankingTotal"
        @current-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <el-drawer
      :title="drawerTitle"
      :visible.sync="drawerVisible"
      size="860px"
      direction="rtl"
      append-to-body
    >
      <div v-loading="drawerLoading" class="drawer-body">
        <div class="drawer-summary-grid">
          <div class="drawer-summary-item">
            <div class="drawer-summary-label">销量</div>
            <div class="drawer-summary-value">{{ drawerSummary.salesQuantity || 0 }}</div>
          </div>
          <div class="drawer-summary-item">
            <div class="drawer-summary-label">收款金额</div>
            <div class="drawer-summary-value income">¥{{ formatMoney(drawerSummary.paidAmount) }}</div>
          </div>
          <div class="drawer-summary-item">
            <div class="drawer-summary-label">退款金额</div>
            <div class="drawer-summary-value expense">¥{{ formatMoney(drawerSummary.refundAmount) }}</div>
          </div>
        </div>

        <el-table :data="orderList" border size="small">
          <el-table-column prop="orderNo" label="订单号" min-width="170" />
          <el-table-column prop="quantity" label="数量" width="70" align="center" />
          <el-table-column label="实付金额" width="110" align="right">
            <template slot-scope="{ row }">¥{{ formatMoney(row.payableAmount) }}</template>
          </el-table-column>
          <el-table-column label="退款金额" width="110" align="right">
            <template slot-scope="{ row }">¥{{ formatMoney(row.refundAmount) }}</template>
          </el-table-column>
          <el-table-column prop="paidAt" label="支付时间" width="170" />
          <el-table-column prop="refundedAt" label="退款时间" width="170" />
          <el-table-column prop="orderStatusText" label="订单状态" width="110" />
          <el-table-column prop="refundStatusText" label="退款状态" width="100" />
        </el-table>

        <div class="pager-wrap">
          <el-pagination
            background
            layout="total, prev, pager, next"
            :current-page="orderQuery.page"
            :page-size="orderQuery.pageSize"
            :total="orderTotal"
            @current-change="handleOrderPageChange"
          />
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { API_BASE_URL } from '../../../../utils/request'
import {
  exportMallProductFlowOrders,
  getMallCategories,
  getMallProductFlowOrders,
  getMallProductFlowSummary,
  getMallProductRanking
} from '../../../../api/mall'

function defaultQuery() {
  return {
    keyword: '',
    categoryId: '',
    sortField: 'salesQuantity',
    page: 1,
    pageSize: 20
  }
}

export default {
  name: 'MallProductFlowPage',
  data() {
    return {
      summary: {},
      rankingList: [],
      rankingTotal: 0,
      rankingLoading: false,
      exporting: false,
      dateRange: [],
      query: defaultQuery(),
      categoryTree: [],
      categoryCascaderProps: {
        value: 'id',
        label: 'categoryName',
        children: 'children',
        leaf: 'leaf',
        emitPath: false
      },
      drawerVisible: false,
      drawerLoading: false,
      drawerProduct: null,
      drawerSummary: {},
      orderList: [],
      orderTotal: 0,
      orderQuery: {
        page: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    categoryOptions() {
      return (this.categoryTree || [])
        .filter(item => Number(item.status) === 1)
        .map(parent => ({
          id: parent.id,
          categoryName: parent.categoryName,
          children: (parent.children || [])
            .filter(child => Number(child.status) === 1)
            .map(child => ({
              id: child.id,
              categoryName: child.categoryName,
              leaf: true
            }))
        }))
        .filter(parent => (parent.children || []).length > 0)
    },
    drawerTitle() {
      const title = this.drawerProduct && this.drawerProduct.title
      return title ? `商品流水 - ${title}` : '商品流水'
    }
  },
  created() {
    this.fetchCategories()
    this.fetchAll()
  },
  methods: {
    buildParams(extra = {}) {
      const params = {
        keyword: this.query.keyword || undefined,
        categoryId: this.query.categoryId || undefined,
        sortField: this.query.sortField || 'salesQuantity',
        page: this.query.page,
        pageSize: this.query.pageSize,
        ...extra
      }
      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key]
        }
      })
      return params
    },
    async fetchCategories() {
      const list = await getMallCategories().catch(() => [])
      this.categoryTree = Array.isArray(list) ? list : []
    },
    async fetchAll() {
      await Promise.all([this.fetchSummary(), this.fetchRanking()])
    },
    async fetchSummary(extra = {}) {
      this.summary = await getMallProductFlowSummary(this.buildParams(extra)).catch(() => ({}))
    },
    async fetchRanking() {
      this.rankingLoading = true
      try {
        const data = await getMallProductRanking(this.buildParams())
        this.rankingList = Array.isArray(data.records) ? data.records : []
        this.rankingTotal = Number(data.total || 0)
        this.query.page = Number(data.page || this.query.page)
        this.query.pageSize = Number(data.pageSize || this.query.pageSize)
      } finally {
        this.rankingLoading = false
      }
    },
    handleSearch() {
      this.query.page = 1
      this.fetchAll()
    },
    resetQuery() {
      this.dateRange = []
      this.query = defaultQuery()
      this.fetchAll()
    },
    async exportFlowOrders() {
      this.exporting = true
      try {
        const params = this.buildParams()
        delete params.page
        delete params.pageSize
        delete params.sortField
        const response = await exportMallProductFlowOrders(params)
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `商品流水对账-${Date.now()}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (error) {
        this.$message.error((error && error.message) || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    handlePageChange(page) {
      this.query.page = page
      this.fetchRanking()
    },
    handlePageSizeChange(pageSize) {
      this.query.pageSize = pageSize
      this.query.page = 1
      this.fetchRanking()
    },
    async openProductFlow(row) {
      this.drawerProduct = row || null
      this.drawerVisible = true
      this.orderQuery.page = 1
      await this.fetchDrawerData()
    },
    async fetchDrawerData() {
      if (!this.drawerProduct || !this.drawerProduct.productId) {
        return
      }
      this.drawerLoading = true
      try {
        const productId = this.drawerProduct.productId
        const [summary, orders] = await Promise.all([
          getMallProductFlowSummary(this.buildParams({ productId })),
          getMallProductFlowOrders(this.buildParams({
            productId,
            page: this.orderQuery.page,
            pageSize: this.orderQuery.pageSize
          }))
        ])
        this.drawerSummary = summary || {}
        this.orderList = Array.isArray(orders.records) ? orders.records : []
        this.orderTotal = Number(orders.total || 0)
      } finally {
        this.drawerLoading = false
      }
    },
    handleOrderPageChange(page) {
      this.orderQuery.page = page
      this.fetchDrawerData()
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    fullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http')) return url
      if (url.startsWith('/uploads/')) {
        const uploadPath = url.replace('/uploads/', '')
        if (uploadPath.includes('/')) {
          return `${API_BASE_URL}${url}`
        }
        return `${API_BASE_URL}/api/common/file/${uploadPath}`
      }
      return `${API_BASE_URL}${url}`
    }
  }
}
</script>

<style scoped>
.product-flow-page {
  min-height: 100%;
}

.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-subtitle {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.summary-item,
.drawer-summary-item {
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}

.summary-label,
.drawer-summary-label {
  color: #909399;
  font-size: 13px;
}

.summary-value,
.drawer-summary-value {
  margin-top: 10px;
  color: #303133;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.drawer-summary-value {
  font-size: 20px;
}

.income,
.amount-income,
.drawer-summary-value.income {
  color: #67c23a;
}

.expense,
.amount-expense,
.drawer-summary-value.expense {
  color: #f56c6c;
}

.toolbar {
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pager-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-cover {
  width: 54px;
  height: 54px;
  border-radius: 6px;
  object-fit: cover;
  background: #f5f7fa;
  flex-shrink: 0;
}

.product-cover-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 12px;
}

.product-info {
  min-width: 0;
}

.product-title {
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.product-subtitle {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.drawer-body {
  padding: 0 4px 24px;
}

.drawer-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
</style>
